import { useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldDescription } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import { BaseForm, FormInput } from "components/BaseForm";
import useAuthorized from "hooks/useAuthorized";
import MetaDatasetService from "services/MetaDataset";
import { STANDALONE_DATASETS_QUERY_KEY } from "routes/meta_datasets/MetaDatasetsPage";
import StandaloneDatasetHealthCheckCard from "routes/meta_datasets/StandaloneDatasetHealthCheckCard";

const LIST_PATH = "/settings/meta_datasets";

const makeSchema = isEdit =>
  z.object({
    name: z.string().trim().min(1, "Name is required"),
    dataset_id: z.string().trim().min(1, "Dataset id is required"),
    access_token: isEdit
      ? z.string().optional()
      : z.string().min(1, "Access token is required"),
  });

const MetaDatasetForm = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";

  const canManage = useAuthorized([
    ACL_RELATIONSHIPS.metaAdAccount.manageAccounts,
  ]);

  const {
    data: dataset,
    isSuccess: datasetLoaded,
    isError: datasetError,
  } = useQuery({
    queryKey: ["meta-standalone-dataset", id],
    queryFn: () => MetaDatasetService.getStandalone(id),
    enabled: isEdit && !!id,
    retry: false,
  });

  const schema = useMemo(() => makeSchema(isEdit), [isEdit]);

  const defaultValues = useMemo(
    () => ({
      name: dataset?.name ?? "",
      dataset_id: dataset?.dataset_id ?? "",
      access_token: "",
    }),
    [dataset]
  );

  const handleSubmit = async values => {
    const payload = { ...values };
    // A blank token on edit retains the stored credential.
    if (isEdit && !payload.access_token) {
      delete payload.access_token;
    }
    if (isEdit) {
      await MetaDatasetService.updateStandalone(id, payload);
    } else {
      await MetaDatasetService.createStandalone(payload);
    }
    queryClient.invalidateQueries({ queryKey: STANDALONE_DATASETS_QUERY_KEY });
    toast.success(isEdit ? "Dataset updated" : "Dataset created");
    navigate(LIST_PATH);
  };

  const handleCancel = () => navigate(LIST_PATH);

  if (!canManage) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (datasetError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <p>{"Couldn't load this dataset. Please try again."}</p>
        <Link to={LIST_PATH}>Back to Meta datasets</Link>
      </div>
    );
  }

  if (isEdit && !datasetLoaded) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-start tw:font-sans tw:text-[var(--color-text-primary)]">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle={isEdit ? "Edit Meta Dataset" : "New Meta Dataset"}
          backLink={{ to: LIST_PATH, label: "Back to Meta datasets" }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardHeader>
              <CardTitle>Dataset</CardTitle>
              <CardDescription>
                A dataset that a client shares without its Meta ad account. The
                dataset id is the Pixel id shown in Events Manager.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput name="name" label="Name" required />
                  <FieldDescription>
                    A label for this dataset inside RVX-LC. It is not sent to
                    Meta.
                  </FieldDescription>
                </div>
                <FormInput name="dataset_id" label="Dataset ID" required />
                <div className="tw:flex tw:flex-col tw:gap-1.5 tw:sm:col-span-2">
                  <FormInput
                    name="access_token"
                    label="Access Token"
                    type="password"
                    // Browsers treat a password field next to a text field as
                    // a login form and fill it with the user's own credentials.
                    autoComplete="new-password"
                    required={!isEdit}
                    placeholder={
                      isEdit
                        ? "Leave blank to keep the current token"
                        : undefined
                    }
                  />
                  <FieldDescription>
                    In Events Manager, select the dataset, open Settings, and
                    choose Generate access token in the Conversions API section.
                  </FieldDescription>
                </div>
              </div>
            </CardContent>
          </Card>
        </BaseForm>
        {isEdit && <StandaloneDatasetHealthCheckCard datasetPk={dataset.id} />}
      </div>
    </div>
  );
};

export default MetaDatasetForm;
